import { useState, useEffect, JSX } from 'react';
import './style.scss';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import { t } from '../../../services/texts.service';
import TrafficSituations from './components/TrafficSituations';
import Trips from './components/Trips';
import CommuterParkingLots from './components/CommuterParkingLots';
import { SettingsHelper } from './helpers/settings.helper';
import { ISettings } from './models/settings.model';
import { DateTime } from 'luxon';
import { DateTimeHelper } from '../../../helpers/date-time.helper';
import { IconColor } from '../../Shared/Icon/icon-color.enum';
import { Icon } from '../../Shared/Icon';
import { IconMotif } from '../../Shared/Icon/icon-motif.enum';
import { IStopArea } from '../../../models/stop-area.model';
import StopAreaSelector from './components/StopAreaSelector';
import Welcome from './components/Welcome';
import Settings from './components/Settings';
import { getDefinedStopAreas } from './helpers/settings.helper';
import { StopAreaType } from './models/stop-area-type.enum';
import type { TransportMode } from '../../../models/transport-mode.enum';

export type IMyStopAreasTexts = object;

export type IMyStopAreasSettings = object;

export type IMyStopAreasProps = IServerSideRendered<IMyStopAreasTexts, IMyStopAreasSettings>;

const HOME_TO_WORK_START_HOUR = 4;
const HOME_TO_WORK_END_HOUR = 12;

function getDefaultSelectedStopAreaType(homeStopAreas: IStopArea[], workStopAreas: IStopArea[]): StopAreaType | undefined {
  const currentHour = DateTime.now().hour;
  const hasHomeStopAreas = homeStopAreas.length > 0;
  const hasWorkStopAreas = workStopAreas.length > 0;
  const shouldPreferHomeStopAreas = currentHour >= HOME_TO_WORK_START_HOUR && currentHour < HOME_TO_WORK_END_HOUR;

  if (shouldPreferHomeStopAreas) {
    if (hasHomeStopAreas) return StopAreaType.Home;
    if (hasWorkStopAreas) return StopAreaType.Work;

    return undefined;
  }

  if (hasWorkStopAreas) return StopAreaType.Work;
  if (hasHomeStopAreas) return StopAreaType.Home;

  return undefined;
}

// eslint-disable-next-line no-empty-pattern
export default function MyStopAreas({}: IMyStopAreasProps) {
  // === CONSTANTS ===
  const UPDATE_INTERVAL_MS = 60000;

  // === STATE ===
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [mySettings, setMySettings] = useState<ISettings>(SettingsHelper.getSettings());
  const [selectedStopAreaType, setSelectedStopAreaType] = useState<StopAreaType | undefined>(() =>
    getDefaultSelectedStopAreaType(getDefinedStopAreas(mySettings.homeStopAreas), getDefinedStopAreas(mySettings.workStopAreas)),
  );
  const [autoRefreshTimestamp, setAutoRefreshTimestamp] = useState<DateTime>(DateTime.now());

  // === VARIABLES ===
  const isFunctionalCookiesAllowed = SettingsHelper.areFunctionalCookiesAllowed();
  const homeStopAreas = getDefinedStopAreas(mySettings.homeStopAreas);
  const workStopAreas = getDefinedStopAreas(mySettings.workStopAreas);
  const mainStopAreas = selectedStopAreaType === StopAreaType.Home ? homeStopAreas : selectedStopAreaType === StopAreaType.Work ? workStopAreas : [];
  const otherStopAreas = selectedStopAreaType === StopAreaType.Home ? workStopAreas : selectedStopAreaType === StopAreaType.Work ? homeStopAreas : [];

  // === EFFECTS ===
  useEffect(() => {
    if (!mySettings.enableAutoRefresh) return;

    const interval = setInterval(() => {
      setAutoRefreshTimestamp(DateTime.now());
    }, UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [mySettings.enableAutoRefresh]);

  // === EVENT HANDLERS ===
  function handleSettingsButtonClick() {
    setShowSettings(true);
  }

  function handleUpdateClick() {
    setAutoRefreshTimestamp(DateTime.now());
  }

  function handleSettingsChange(settings: ISettings): void {
    let settingsToSave = { ...settings };
    const updatedHomeStopAreas = getDefinedStopAreas(settings.homeStopAreas);
    const updatedWorkStopAreas = getDefinedStopAreas(settings.workStopAreas);
    let newSelectedStopAreaType = selectedStopAreaType;

    // Check if the welcome flow is completed
    if (!settings.welcomeCompleted && updatedHomeStopAreas.length > 0 && updatedWorkStopAreas.length > 0) {
      settingsToSave = { ...settingsToSave, welcomeCompleted: true };
      newSelectedStopAreaType = getDefaultSelectedStopAreaType(updatedHomeStopAreas, updatedWorkStopAreas);
    }

    // Check if both stop areas are cleared, if so show the welcome flow again
    if (updatedHomeStopAreas.length === 0 && updatedWorkStopAreas.length === 0) {
      settingsToSave = { ...settingsToSave, welcomeCompleted: false };
    }

    // Check if the previously selected stop area type is cleared, if so select the other one
    if (newSelectedStopAreaType === StopAreaType.Home && updatedHomeStopAreas.length === 0) newSelectedStopAreaType = updatedWorkStopAreas.length > 0 ? StopAreaType.Work : undefined;

    if (newSelectedStopAreaType === StopAreaType.Work && updatedWorkStopAreas.length === 0) newSelectedStopAreaType = updatedHomeStopAreas.length > 0 ? StopAreaType.Home : undefined;

    if (!newSelectedStopAreaType) newSelectedStopAreaType = getDefaultSelectedStopAreaType(updatedHomeStopAreas, updatedWorkStopAreas);

    SettingsHelper.saveSettings(settingsToSave);
    setMySettings(settingsToSave);
    setSelectedStopAreaType(newSelectedStopAreaType);
  }

  // === RENDERING ===
  if (!isFunctionalCookiesAllowed) {
    return (
      <div>
        {t(
          'För att använda Mina Hållplatser måste du tillåta funktionella cookies. Se länken "Om Cookies" i footern.',
          'To use My Stop Areas, you need to allow functional cookies. See the "About Cookies" link in the footer.',
        )}
      </div>
    );
  }

  return (
    <div className="my-stop-areas">
      {!mySettings.welcomeCompleted && !showSettings && <Welcome mySettings={mySettings} onChange={handleSettingsChange} />}
      {mySettings.welcomeCompleted && !showSettings && (
        <>
          <h2 className="visually-hidden">
            {t.stopArea} {mainStopAreas.map((stopArea) => stopArea.name).join(', ')}
          </h2>
          <div className="d-flex justify-content-end mb-2">
            <button className="btn btn-tertiary btn-tertiary-position-right d-flex align-items-center" onClick={handleSettingsButtonClick}>
              <Icon className="pe-1" motif={IconMotif.Gear} color={IconColor.Blue1000} altText=""></Icon>
              <span>{t('Inställningar', 'Settings')}</span>
            </button>
          </div>
          <StopAreaSelector selectedStopAreaType={selectedStopAreaType} homeStopAreas={mySettings.homeStopAreas} workStopAreas={mySettings.workStopAreas} onSelect={setSelectedStopAreaType} />
          {mainStopAreas.length > 0 && (
            <div className="mt-4">
              <StopArea mainStopAreas={mainStopAreas} otherStopAreas={otherStopAreas} mySettings={mySettings} autoRefreshTimestamp={autoRefreshTimestamp} onUpdateClick={handleUpdateClick} />
            </div>
          )}
        </>
      )}
      {showSettings && <Settings mySettings={mySettings} onChange={handleSettingsChange} onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// === SUB-COMPONENTS ===
function StopArea({
  mainStopAreas,
  otherStopAreas,
  mySettings,
  autoRefreshTimestamp,
  onUpdateClick,
}: {
  mainStopAreas: IStopArea[];
  otherStopAreas: IStopArea[];
  mySettings: ISettings;
  autoRefreshTimestamp: DateTime;
  onUpdateClick: () => void;
}): JSX.Element {
  const [upcomingTripLineGids, setUpcomingTripLineGids] = useState<string[] | undefined>(undefined);
  const [upcomingTripTransportModes, setUpcomingTripTransportModes] = useState<TransportMode[] | undefined>(undefined);

  return (
    <>
      <div className="d-flex flex-column gap-4 mb-4">
        {mySettings.showTrips && (
          <Trips
            from={mainStopAreas}
            to={otherStopAreas}
            autoRefreshTimestamp={autoRefreshTimestamp}
            onJourneysLineGidsChange={setUpcomingTripLineGids}
            onJourneysTransportModesChange={setUpcomingTripTransportModes}
            {...mySettings}
          />
        )}
        {mySettings.showTrafficInformation && (
          <TrafficSituations
            stopAreas={mainStopAreas}
            autoRefreshTimestamp={autoRefreshTimestamp}
            journeyLineGids={mySettings.showTrips && mySettings.filterTrafficSituationsByTrips ? upcomingTripLineGids : undefined}
            journeyTransportModes={mySettings.showTrips && mySettings.filterTrafficSituationsByTrips ? upcomingTripTransportModes : undefined}
          />
        )}
        {mySettings.showCommuterParkingLots && <CommuterParkingLots stopAreas={mainStopAreas} autoRefreshTimestamp={autoRefreshTimestamp} />}
      </div>
      <div className="d-flex flex-column align-items-center gap-1">
        <div className="text-center">
          {t('Uppdaterades', 'Updated')} {DateTimeHelper.toTime(autoRefreshTimestamp)}
        </div>
        <button className="btn btn-tertiary" onClick={onUpdateClick} aria-label={t('Uppdatera nu', 'Update now')}>
          <Icon className="p-1" motif={IconMotif.ArrowsRotate} color={IconColor.Blue1000} altText=""></Icon>
          {t('Uppdatera nu', 'Update now')}
        </button>
      </div>
    </>
  );
}
